/** DOM overlay root: HUD for the current mode, the view stack (modals, overlays, screens), toasts and the loading veil. */
import { render, type JSX } from 'preact';
import { useEffect, useReducer } from 'preact/hooks';
import { t } from '../i18n/index.js';
import type { Services } from '../services/index.js';
import type { PlayerState } from '../services/state.js';
import type { UiBridge } from './bridge.js';
import { MapActions, PlayBar, ShopSheet, TopBar } from './hud.js';
import { Layer } from './layer.js';
import { ModalView, type ModalCtx } from './modals.js';

export interface OverlayDeps {
  ui: UiBridge;
  services: Services;
  fast: () => boolean;
  onSettings: (patch: Partial<PlayerState['settings']>) => void;
  openSettings: () => void;
}

function useTick(subscribe: (fn: () => void) => () => void): void {
  const [, force] = useReducer((n: number) => n + 1, 0);
  useEffect(() => subscribe(() => force(0)), []);
}

function Root({ d }: { d: OverlayDeps }): JSX.Element {
  useTick((fn) => d.ui.subscribe(fn));
  useTick((fn) => d.services.store.subscribe(fn));
  const s = d.services.store.state;
  const { hud } = d.ui;
  const ctx: ModalCtx = {
    s,
    fast: d.fast(),
    reducedMotion: s.settings.reducedMotion,
    onSettings: d.onSettings,
    toast: (text) => d.ui.toast(text),
  };
  const stack = d.ui.modals;
  return (
    <div class={`overlay mode-${hud.mode} ${s.settings.reducedMotion ? 'reduced' : ''}`}>
      {/* a feature's in-round HUD (PlayHud.extra): first, so the playbar, the view stack, the toasts and the veil paint over it */}
      {hud.mode === 'play' && hud.play?.extra ? hud.play.extra() : null}
      {(hud.mode === 'map' || hud.mode === 'shop') && (
        <TopBar
          s={s}
          pulse={hud.coinPulse}
          onSettings={hud.onSettings ?? d.openSettings}
          onStore={hud.shop?.visit ? null : hud.onStore}
          onBack={hud.mode === 'shop' ? hud.shop?.onBack : null}
        />
      )}
      {hud.mode === 'map' && hud.map && <MapActions m={hud.map} />}
      {hud.mode === 'shop' && hud.shop && <ShopSheet h={hud.shop} s={s} />}
      {hud.mode === 'play' && hud.play && <PlayBar p={hud.play} />}
      {stack.map((m, i) => (
        <div class={`layer ${i === stack.length - 1 ? 'top' : 'under'}`} key={m.id} aria-hidden={i === stack.length - 1 ? undefined : 'true'}>
          <Layer.Provider value={{ id: m.id, ui: d.ui }}>
            <ModalView e={m} c={ctx} close={(r: unknown) => d.ui.close(m.id, r)} />
          </Layer.Provider>
        </div>
      ))}
      <div class="toasts" aria-live="polite">
        {d.ui.toasts.map((t) => (
          <div class="toast" key={t.id} data-testid="toast">
            {t.text}
          </div>
        ))}
      </div>
      {d.ui.busy && <div class="veil" aria-busy="true" />}
      {d.ui.adLoading && (
        <div class="veil ad-loading" aria-busy="true" data-testid="ad-loading">
          <span class="spinner" aria-hidden="true" />
          <p>{t('ad.loading')}</p>
        </div>
      )}
    </div>
  );
}

export function mountOverlay(el: HTMLElement, d: OverlayDeps): void {
  render(<Root d={d} />, el);
}
